import { Link, useLocation } from "react-router-dom";
import { Phone, Mail, MapPin, ExternalLink } from "lucide-react";
import { SITE, SERVICES, REGION_ORDER, locationsByRegion, INDUSTRY_LINKS } from "../data/site";
import ARTICLES from "../data/articles";

export default function Footer() {
  const isHome = useLocation().pathname === "/";
  return (
    <footer data-testid="site-footer" className="border-t border-white/10 bg-coal/60">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14 sm:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-10">
          <div>
            <div className="flex items-center gap-3">
              <span className="w-9 h-9 bg-amber flex items-center justify-center font-display font-black text-ink text-xl leading-none">
                X
              </span>
              <span className="leading-none">
                <span className="block font-display font-extrabold uppercase tracking-wider text-white text-lg">
                  Xtreme Protection
                </span>
                <span className="block font-mono text-[9px] uppercase tracking-[0.3em] text-amber mt-1">
                  England Security Services
                </span>
              </span>
            </div>
            <p className="mt-5 text-sm text-gray-400 leading-relaxed">
              SIA-licensed security guard services across 120+ towns and cities in
              England. Manned guarding, mobile patrols, CCTV monitoring, door
              supervisors, event security and more — 24/7 nationwide cover.
            </p>
            <div className="mt-6 space-y-2">
              <a
                href={SITE.phoneHref}
                data-testid="footer-phone-link"
                className="flex items-center gap-2 text-sm text-gray-300 hover:text-amber transition-colors"
              >
                <Phone className="w-4 h-4 text-amber" /> {SITE.phone}
              </a>
              <a
                href={SITE.emailHref}
                data-testid="footer-email-link"
                className="flex items-center gap-2 text-sm text-gray-300 hover:text-amber transition-colors"
              >
                <Mail className="w-4 h-4 text-amber" /> {SITE.email}
              </a>
              <span className="flex items-center gap-2 text-sm text-gray-500">
                <MapPin className="w-4 h-4 text-amber" /> HQ: {SITE.address} — covering all of England
              </span>
              <a
                href={SITE.mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-gbp-link"
                className="flex items-center gap-2 text-sm text-gray-300 hover:text-amber transition-colors"
              >
                <ExternalLink className="w-4 h-4 text-amber" /> Google Business Profile
              </a>
            </div>
          </div>

          <div>
            <h3 className="font-display font-bold uppercase tracking-widest text-sm text-white mb-4">
              Security Services
            </h3>
            <ul className="space-y-2">
              {SERVICES.map((s) => (
                <li key={s.slug}>
                  <Link
                    to={`/services/${s.slug}`}
                    data-testid={`footer-service-${s.slug}`}
                    className="text-sm text-gray-400 hover:text-amber transition-colors"
                  >
                    {s.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-2">
            <h3 className="font-display font-bold uppercase tracking-widest text-sm text-white mb-4">
              Areas We Cover in England
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6">
              {REGION_ORDER.map((region) => (
                <div key={region}>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-amber mb-2">
                    {region}
                  </p>
                  <p className="text-xs leading-6 text-gray-500">
                    {locationsByRegion(region).map((l, i) => (
                      <span key={l.slug}>
                        <Link
                          to={`/locations/${l.slug}`}
                          className="hover:text-amber transition-colors"
                        >
                          Security {l.name}
                        </Link>
                        {i < locationsByRegion(region).length - 1 && " · "}
                      </span>
                    ))}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-white/10">
          <p className="font-mono text-[10px] uppercase tracking-widest text-white/40 mb-3">
            The Intel Desk — Security Guides &amp; Resources
          </p>
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            <Link to="/resources" className="text-xs text-gray-400 hover:text-amber transition-colors font-semibold">
              All Guides
            </Link>
            {ARTICLES.map((a) => (
              <Link
                key={a.slug}
                to={`/resources/${a.slug}`}
                className="text-xs text-gray-500 hover:text-amber transition-colors"
              >
                {a.title}
              </Link>
            ))}
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-white/10">
          <p className="font-mono text-[10px] uppercase tracking-widest text-white/40 mb-3">
            Industry &amp; Accreditation Resources
          </p>
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            {INDUSTRY_LINKS.map((l) => (
              <a
                key={l.url}
                href={l.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-amber transition-colors"
              >
                <ExternalLink className="w-3 h-3" />
                {l.name}
              </a>
            ))}
          </div>
        </div>

        <div className="mt-10 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <p className="font-mono text-[10px] uppercase tracking-widest text-gray-500">
            © 2026 {SITE.name} — {SITE.tagline}. All rights reserved.
          </p>
          <p className="font-mono text-[10px] uppercase tracking-widest text-gray-500">
            SIA Licensed · BS 7858 Vetted · 24/7 Command
          </p>
        </div>
        {isHome && (
          <p className="mt-4 font-mono text-[10px] tracking-widest text-gray-500" data-testid="footer-group-line">
            Part of the{" "}
            <a
              href="https://xtremeprotection.uk"
              className="text-amber hover:text-white transition-colors"
              data-testid="footer-group-link"
            >
              Xtreme Protection group
            </a>
          </p>
        )}
      </div>
    </footer>
  );
}
